import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Section, Eyebrow, H2, GlassCard } from "../../components/site/Primitives";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { log } from "../../lib/logger";
import { CheckCircle2, Circle, FileText } from "lucide-react";
import { InvestorMap } from "../../components/site/Maps";

export default function InvestorPortal() {
  const { user, loading } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/portal/investor/overview").then((r) => setD(r.data)).catch((e) => log.warn("investor overview fetch failed", e?.message)); }, []);
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!d) return null;

  return (
    <Section className="pt-40">
      <Eyebrow>Investor Portal</Eyebrow>
      <H2>{d.phase_1.name}</H2>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <GlassCard className="lg:col-span-8 p-8">
          <div className="text-xs uppercase tracking-[0.2em] text-gold mb-3">Phase 1 Progress</div>
          <div className="flex items-end gap-4 mb-2"><div className="font-display text-5xl">{d.phase_1.progress_pct}%</div></div>
          <div className="h-2 bg-white/10"><div className="h-full bg-gold" style={{ width: `${d.phase_1.progress_pct}%` }} /></div>
          <div className="mt-6 grid grid-cols-2 gap-6">
            <Stat label="Capital Target" value={`$${(d.phase_1.target_capital / 1e6).toFixed(1)}M`} />
            <Stat label="Committed" value={`$${(d.phase_1.committed / 1e6).toFixed(1)}M`} />
          </div>
        </GlassCard>
        <GlassCard className="lg:col-span-4 p-8">
          <div className="text-xs uppercase tracking-[0.2em] text-gold mb-3">Projected ROI</div>
          <div className="space-y-4 mt-4">
            <Stat label="5-Year" value={d.projected_roi["5_year"]} />
            <Stat label="10-Year" value={d.projected_roi["10_year"]} />
          </div>
        </GlassCard>
      </div>

      <div className="mt-10">
        <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-gold mb-2">Confidential Parcel Layer</div>
            <h3 className="font-display text-2xl">Investor-only acquisition map</h3>
          </div>
          <div className="text-xs text-white/40 max-w-md">
            Parcel-level acquisition data, zoning status, and milestones — restricted to admin and investor roles.
          </div>
        </div>
        <InvestorMap height={520} />
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <GlassCard className="lg:col-span-7 p-8">
          <div className="text-xs uppercase tracking-[0.2em] text-gold mb-4">Milestones</div>
          <ul className="space-y-3">
            {d.phase_1.milestones.map((m) => (
              <li key={m.id} className="flex items-center gap-3">
                {m.done ? <CheckCircle2 className="text-gold" size={18} /> : <Circle className="text-white/30" size={18} />}
                <span className={m.done ? "" : "text-white/60"}>{m.label}</span>
              </li>
            ))}
          </ul>
        </GlassCard>
        <GlassCard className="lg:col-span-5 p-8">
          <div className="text-xs uppercase tracking-[0.2em] text-gold mb-4">Documents</div>
          <ul className="space-y-3">
            {d.documents.map((doc) => (
              <li key={doc.name} className="flex items-center gap-3 border border-white/10 p-3">
                <FileText className="text-gold" size={16} />
                <div className="flex-1">
                  <div className="text-sm">{doc.name}</div>
                  <div className="text-xs text-white/40">{doc.size}</div>
                </div>
              </li>
            ))}
          </ul>
        </GlassCard>
      </div>
    </Section>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-1">{label}</div>
      <div className="font-display text-2xl text-white">{value}</div>
    </div>
  );
}
